type AnalyticsValue = string | number | boolean;

export type AnalyticsParameters = Record<string, AnalyticsValue | null | undefined>;
type CleanAnalyticsParameters = Record<string, AnalyticsValue>;

interface SiteSearchParameters {
  searchTerm: string;
  searchLocation?: string;
  resultsCount?: number;
}

interface SearchResultClickParameters extends SiteSearchParameters {
  resultTitle: string;
  resultUrl: string;
  resultPosition: number;
  resultType?: string;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __bcpfAnalyticsInitialized?: boolean;
  }
}

const SCROLL_THRESHOLDS = [50, 75, 90] as const;
const RESOURCE_EXTENSIONS = new Set([
  "csv", "doc", "docx", "pdf", "ppt", "pptx", "xls", "xlsx", "zip",
]);

let reachedScrollThresholds = new Set<number>();

export const normalizeSearchTerm = (value: string): string =>
  value.trim().replace(/\s+/g, " ").slice(0, 100);

const cleanAnalyticsParameters = (
  parameters: AnalyticsParameters
): CleanAnalyticsParameters => (
  Object.fromEntries(
    Object.entries(parameters)
      .filter(([, value]) => value !== undefined && value !== null && value !== "")
  ) as CleanAnalyticsParameters
);

const getPageContext = (): AnalyticsParameters => ({
  page_location: window.location.href,
  page_title: document.title,
});

const ensureDataLayer = (): unknown[] => {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

const ensureGoogleTag = (): ((...args: unknown[]) => void) => {
  const dataLayer = ensureDataLayer();

  if (typeof window.gtag !== "function") {
    window.gtag = (...args: unknown[]) => {
      dataLayer.push(args);
    };
  }

  return window.gtag;
};

/** Push a privacy-safe, site-owned event to Google Tag Manager. */
export const trackEvent = (
  event: string,
  parameters: AnalyticsParameters = {}
): void => {
  if (typeof window === "undefined") return;

  const cleanParameters = cleanAnalyticsParameters({ ...getPageContext(), ...parameters });

  ensureDataLayer().push({ event, ...cleanParameters });
};

/** Send an event in the native Google tag/GA4 command format. */
export const trackGoogleAnalyticsEvent = (
  event: string,
  parameters: AnalyticsParameters = {}
): void => {
  if (typeof window === "undefined") return;

  const cleanParameters = cleanAnalyticsParameters({ ...getPageContext(), ...parameters });
  ensureGoogleTag()("event", event, cleanParameters);
};

const getSearchParameters = ({
  searchTerm,
  searchLocation = "search_results_page",
  resultsCount,
}: SiteSearchParameters): AnalyticsParameters => {
  const normalizedTerm = normalizeSearchTerm(searchTerm);

  return {
    search_term: normalizedTerm,
    search_query: normalizedTerm,
    site_search_term: normalizedTerm,
    search_location: searchLocation,
    results_count: resultsCount,
    search_results_count: resultsCount,
  };
};

export const trackSiteSearch = (parameters: SiteSearchParameters): void => {
  const searchParameters = getSearchParameters(parameters);

  trackEvent("view_search_results", searchParameters);
  trackEvent("search", searchParameters);
  trackGoogleAnalyticsEvent("view_search_results", searchParameters);
  trackGoogleAnalyticsEvent("search", searchParameters);
};

export const trackSearchResultClick = ({
  resultTitle,
  resultUrl,
  resultPosition,
  resultType,
  ...searchParameters
}: SearchResultClickParameters): void => {
  const normalizedSearchParameters = getSearchParameters(searchParameters);

  const resultParameters = {
    ...normalizedSearchParameters,
    result_title: resultTitle,
    search_result_title: resultTitle,
    link_text: resultTitle,
    result_url: resultUrl,
    search_result_url: resultUrl,
    link_url: resultUrl,
    result_position: resultPosition,
    search_result_position: resultPosition,
    result_type: resultType,
    search_result_type: resultType,
  };

  trackEvent("select_search_result", resultParameters);
  trackGoogleAnalyticsEvent("select_search_result", resultParameters);
};

/** Track an event at most once per browser tab session. */
export const trackEventOnce = (
  event: string,
  parameters: AnalyticsParameters = {},
  key = event
): void => {
  if (typeof window === "undefined") return;

  const storageKey = `analytics:${key}`;
  if (window.sessionStorage.getItem(storageKey)) return;

  window.sessionStorage.setItem(storageKey, "1");
  trackEvent(event, parameters);
};

export const resetScrollDepthTracking = (): void => {
  reachedScrollThresholds = new Set<number>();
};

const getResourceDetails = (anchor: HTMLAnchorElement) => {
  const url = new URL(anchor.href, window.location.href);
  const fileName = decodeURIComponent(url.pathname.split("/").filter(Boolean).pop() || "");
  const extension = fileName.includes(".") ? fileName.split(".").pop()?.toLowerCase() || "" : "";
  const resourceType = anchor.dataset.resourceType || extension;
  const isResource = anchor.hasAttribute("download")
    || anchor.hasAttribute("data-analytics-resource")
    || RESOURCE_EXTENSIONS.has(extension);

  return { fileName, resourceType, isResource, url: url.href };
};

/** Install delegated click and scroll listeners once for the Gatsby browser session. */
export const initializeGlobalAnalytics = (): void => {
  if (typeof window === "undefined" || window.__bcpfAnalyticsInitialized) return;
  window.__bcpfAnalyticsInitialized = true;

  document.addEventListener("click", (event) => {
    const target = event.target as Element | null;
    const anchor = target?.closest<HTMLAnchorElement>("a[href]");
    if (!anchor) return;

    const resource = getResourceDetails(anchor);
    if (resource.isResource) {
      trackEvent("resource_download", {
        file_name: anchor.dataset.fileName || resource.fileName,
        file_url: resource.url,
        resource_type: resource.resourceType || "other",
      });
      return;
    }

    if (anchor.hasAttribute("data-analytics-important-external")) {
      trackEvent("important_external_link_click", {
        link_text: (anchor.textContent || anchor.getAttribute("aria-label") || "").trim().slice(0, 150),
        link_url: anchor.href,
      });
    }
  });

  window.addEventListener("scroll", () => {
    const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollableHeight <= 0) return;

    const percent = Math.min(100, Math.round((window.scrollY / scrollableHeight) * 100));
    SCROLL_THRESHOLDS.forEach((threshold) => {
      if (percent >= threshold && !reachedScrollThresholds.has(threshold)) {
        reachedScrollThresholds.add(threshold);
        trackEvent("scroll_depth", { percent_scrolled: threshold });
        if (threshold === 90) {
          trackEvent("scroll_depth_90", { percent_scrolled: threshold });
        }
      }
    });
  }, { passive: true });
};
